import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { HiOutlineCash, HiOutlineChartBar, HiOutlineArrowSmUp, HiOutlineArrowSmDown } from 'react-icons/hi';
import { Tooltip } from 'react-tooltip';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis } from 'recharts';
import TokenStack from './TokenStack';
import ChainIcon from './ChainIcon';
import ProtocolIcon from './ProtocolIcon';
import { PROTOCOL_COLORS, CHAIN_COLORS, RISK_MAP } from '../data';

import { useTheme } from '../theme';
import { useIsMobile } from '../useMediaQuery';

/* -- Scoring helpers -------------------------------------------------------- */

function scorePool(pool) {
  let yieldScore = 50;
  const pct30 = pool.apyPct30D ?? 0;
  const pct7  = pool.apyPct7D ?? 0;

  if (pct30 >= 0 && pct30 <= 10)       yieldScore = 90;
  else if (pct30 > 10 && pct30 <= 30)  yieldScore = 70;
  else if (pct30 > 30)                 yieldScore = 40;
  else if (pct30 >= -10 && pct30 < 0)  yieldScore = 60;
  else                                 yieldScore = 30;

  if (pct7 < -20) yieldScore *= 0.5;
  else if (pct7 < -10) yieldScore *= 0.7;
  else if (pct7 < 0)   yieldScore *= 0.9;

  const volTvlRatio = pool.tvl > 0 ? (pool.volume24h || 0) / pool.tvl : 0;
  const volumeScore = Math.min(100, volTvlRatio * 1000);

  const tvlScore = pool.tvl > 0
    ? Math.min(100, Math.max(0, (Math.log10(pool.tvl) - 4) * 20))
    : 0;

  const riskScore = pool.risk === "LOW" ? 100 : pool.risk === "MED" ? 50 : 10;

  const total = (
    yieldScore  * 0.40 +
    volumeScore * 0.30 +
    tvlScore    * 0.20 +
    riskScore   * 0.10
  );

  return {
    total: Math.round(Math.min(100, Math.max(0, total))),
    yieldScore: Math.round(yieldScore),
    volumeScore: Math.round(Math.min(100, volumeScore)),
    tvlScore: Math.round(tvlScore),
    riskScore,
    volTvlRatio,
  };
}

function scoredPools(pools) {
  return pools
    .filter(p => p.apr > 0 && p.tvl > 100000)
    .map(p => ({ ...p, score: scorePool(p) }))
    .sort((a, b) => b.score.total - a.score.total);
}

/* For Hidden Gems we need pools scored without the $100K TVL floor */
function scoredPoolsAllTvl(pools) {
  return pools
    .filter(p => p.apr > 0)
    .map(p => ({ ...p, score: scorePool(p) }))
    .sort((a, b) => b.score.total - a.score.total);
}

/* -- Category Styles -------------------------------------------------------- */

const CATEGORY_STYLES = {
  "High Yield Stable": { color: "#0D7A4E", bg: "#E8F8F2", border: "#A8DEC8", icon: "\uD83D\uDEE1", description: "Stablecoin pools with the best risk-adjusted returns" },
  "Volume Kings":      { color: "#8A6A00", bg: "#FFF8E0", border: "#E0CC70", icon: "\u26A1", description: "Highest organic trading activity relative to pool size" },
  "Steady Earners":    { color: "#5470D4", bg: "#EEF1FD", border: "#B0C0E8", icon: "\uD83D\uDCC8", description: "Consistent returns with minimal APR volatility" },
  "Hidden Gems":       { color: "#8247E5", bg: "#F0EDFD", border: "#C4B5F0", icon: "\uD83D\uDC8E", description: "High-quality pools under $2M TVL \u2014 early opportunities" },
  "Blue Chips":        { color: "#5470D4", bg: "#EEF1FD", border: "#B0C0E8", icon: "\uD83C\uDFE6", description: "Large, established stablecoin pools for conservative yields" },
  "Rising Stars":      { color: "#E8471A", bg: "#FDEDE8", border: "#F0A0A8", icon: "\uD83D\uDE80", description: "Pools with accelerating APR growth over 7 and 30 days" },
};

/* -- Why this pick helper --------------------------------------------------- */

function getWhyText(score) {
  const reasons = [];
  if (score.yieldScore >= 80) reasons.push("Stable and consistent yield");
  if (score.volumeScore >= 70) reasons.push("High organic trading volume");
  if (score.tvlScore >= 80) reasons.push("Deep liquidity, low slippage");
  if (score.riskScore >= 80) reasons.push("Low risk stablecoin pair");
  if (reasons.length === 0) {
    const best = [
      { s: score.yieldScore, t: "Decent yield stability" },
      { s: score.volumeScore, t: "Active trading volume" },
      { s: score.tvlScore, t: "Reasonable liquidity" },
      { s: score.riskScore, t: "Moderate risk profile" },
    ].sort((a, b) => b.s - a.s);
    reasons.push(best[0].t, best[1].t);
  }
  return reasons.slice(0, 2).join(" \u00B7 ");
}

/* -- Mini Radar Chart ------------------------------------------------------- */

function MiniRadar({ score, colors }) {
  const scoreColor = score.total >= 70 ? colors.green : score.total >= 40 ? colors.gold : colors.red;
  const data = [
    { dim: "Yield", value: score.yieldScore },
    { dim: "Vol", value: score.volumeScore },
    { dim: "TVL", value: score.tvlScore },
    { dim: "Risk", value: score.riskScore },
  ];

  return (
    <RadarChart width={100} height={100} data={data} cx={50} cy={50} outerRadius={35}>
      <PolarGrid stroke={colors.borderSubtle} />
      <PolarAngleAxis
        dataKey="dim"
        tick={{ fontSize: 8, fontFamily: "'IBM Plex Mono', monospace", fill: colors.labelMuted }}
      />
      <Radar
        dataKey="value"
        stroke={scoreColor}
        fill={scoreColor}
        fillOpacity={0.25}
        strokeWidth={1.5}
      />
    </RadarChart>
  );
}

/* -- Score Bar component ---------------------------------------------------- */

function ScoreBar({ score, size = "normal", animated = false }) {
  const { colors } = useTheme();
  const barHeight = size === "small" ? 4 : 6;
  const barWidth = size === "small" ? 60 : 80;
  const color = score >= 70 ? colors.green : score >= 40 ? colors.gold : colors.red;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{
        width: barWidth,
        height: barHeight,
        borderRadius: barHeight / 2,
        background: colors.scoreTrack,
        overflow: "hidden",
      }}>
        {animated ? (
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${score}%` }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
            style={{
              height: "100%",
              borderRadius: barHeight / 2,
              background: color,
              boxShadow: `0 0 6px ${color}60`,
            }}
          />
        ) : (
          <div style={{
            width: `${score}%`,
            height: "100%",
            borderRadius: barHeight / 2,
            background: color,
            transition: "width 0.3s ease",
          }} />
        )}
      </div>
      <span style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: size === "small" ? 9 : 10,
        fontWeight: 600,
        color,
      }}>
        {score}
      </span>
    </div>
  );
}

/* -- Pick Card -------------------------------------------------------------- */

function PickCard({ pool, onSelect, isMobile, index = 0 }) {
  const { colors } = useTheme();
  const protocolColor = PROTOCOL_COLORS[pool.protocol] || colors.textPrimary;
  const chainColor    = CHAIN_COLORS[pool.chain] || colors.textSecondary;
  const riskStyle     = RISK_MAP[pool.risk] || RISK_MAP.MED;
  const totalScore    = pool.score.total;
  const scoreColor    = totalScore >= 70 ? colors.green : totalScore >= 40 ? colors.gold : colors.red;
  const tooltipId     = `score-tip-${pool.id}`;

  const fmtRaw = (n) => {
    if (n >= 1e9) return { end: n / 1e9, decimals: 2, prefix: "$", suffix: "B" };
    if (n >= 1e6) return { end: n / 1e6, decimals: 1, prefix: "$", suffix: "M" };
    if (n >= 1e3) return { end: n / 1e3, decimals: 0, prefix: "$", suffix: "K" };
    return { end: n, decimals: 0, prefix: "$", suffix: "" };
  };

  const tvlParts = fmtRaw(pool.tvl);
  const volParts = fmtRaw(pool.volume24h || 0);

  return (
    <motion.div
      onClick={() => onSelect(pool)}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08, ease: "easeOut" }}
      whileHover={{ scale: 1.03, y: -4, boxShadow: `0 8px 24px ${scoreColor}30` }}
      whileTap={{ scale: 0.98 }}
      style={{
        background: colors.surface,
        borderRadius: 12,
        padding: isMobile ? 16 : 20,
        boxShadow: `0 1px 4px ${colors.shadow}`,
        border: `1px solid ${colors.border}`,
        borderTop: `3px solid ${scoreColor}`,
        cursor: "pointer",
        minWidth: isMobile ? 'auto' : 280,
        flex: isMobile ? 'none' : "0 0 auto",
        width: isMobile ? '100%' : 'auto',
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Subtle gradient background tint */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 80,
        background: `linear-gradient(180deg, ${scoreColor}08 0%, transparent 100%)`,
        pointerEvents: "none",
      }} />

      {/* Header row: tokens + name + radar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 14, position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
          <TokenStack tokens={pool.tokens} size={28} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: 16,
              color: colors.textPrimary,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}>
              {pool.name}
            </div>
            <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9,
                color: protocolColor,
                background: `${protocolColor}12`,
                border: `1px solid ${protocolColor}30`,
                padding: "1px 6px",
                borderRadius: 3,
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
              }}>
                <ProtocolIcon protocol={pool.protocol} size={10} />
                {pool.protocol}
              </span>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9,
                color: chainColor,
                background: `${chainColor}12`,
                border: `1px solid ${chainColor}30`,
                padding: "1px 6px",
                borderRadius: 3,
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
              }}>
                <ChainIcon chain={pool.chain} size={10} />
                {pool.chain}
              </span>
            </div>
          </div>
        </div>

        {/* Mini RadarChart top-right */}
        <div style={{ flexShrink: 0, marginTop: -6, marginRight: -6 }}>
          <MiniRadar score={pool.score} colors={colors} />
        </div>
      </div>

      {/* APR with CountUp */}
      <div style={{
        fontFamily: "'DM Serif Display', serif",
        fontSize: 34,
        color: pool.apr > 20 ? colors.gold : colors.green,
        marginBottom: 6,
        lineHeight: 1,
      }}>
        <CountUp end={pool.apr || 0} duration={1.2} decimals={2} suffix="%" preserveValue />
      </div>

      {/* "Why this pick" text */}
      <div style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 10,
        fontStyle: "italic",
        color: colors.labelMuted,
        marginBottom: 12,
        lineHeight: 1.4,
      }}>
        {getWhyText(pool.score)}
      </div>

      {/* Score indicator with tooltip */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 12,
        paddingBottom: 12,
        borderBottom: `1px solid ${colors.borderSubtle}`,
      }}>
        <span
          data-tooltip-id={tooltipId}
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9,
            color: colors.labelLight,
            letterSpacing: 1,
            fontWeight: 500,
            cursor: "default",
          }}
        >
          SCORE
        </span>
        <ScoreBar score={totalScore} animated />
        <Tooltip id={tooltipId} place="top" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>
          <div>Yield: {pool.score.yieldScore}</div>
          <div>Volume: {pool.score.volumeScore}</div>
          <div>TVL: {pool.score.tvlScore}</div>
          <div>Risk: {pool.score.riskScore}</div>
        </Tooltip>
      </div>

      {/* Metrics with icons and CountUp */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {/* TVL */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            color: colors.labelLight,
            letterSpacing: 1,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}>
            <HiOutlineCash size={12} /> TVL
          </span>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11,
            color: colors.textSecondary,
            fontWeight: 500,
          }}>
            <CountUp
              end={tvlParts.end}
              duration={1.0}
              decimals={tvlParts.decimals}
              prefix={tvlParts.prefix}
              suffix={tvlParts.suffix}
              preserveValue
            />
          </span>
        </div>

        {/* Volume */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            color: colors.labelLight,
            letterSpacing: 1,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}>
            <HiOutlineChartBar size={12} /> VOL 24H
          </span>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11,
            color: colors.textSecondary,
            fontWeight: 500,
          }}>
            <CountUp
              end={volParts.end}
              duration={1.0}
              decimals={volParts.decimals}
              prefix={volParts.prefix}
              suffix={volParts.suffix}
              preserveValue
            />
          </span>
        </div>

        {/* APY 7D */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            color: colors.labelLight,
            letterSpacing: 1,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}>
            {pool.apyPct7D != null && pool.apyPct7D >= 0
              ? <HiOutlineArrowSmUp size={12} color={colors.green} />
              : <HiOutlineArrowSmDown size={12} color={colors.red} />
            }
            APY 7D
          </span>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11,
            color: pool.apyPct7D != null && pool.apyPct7D >= 0 ? colors.green : colors.red,
            fontWeight: 500,
          }}>
            {pool.apyPct7D != null ? `${pool.apyPct7D > 0 ? "+" : ""}${pool.apyPct7D.toFixed(1)}%` : "\u2014"}
          </span>
        </div>

        {/* APY 30D */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            color: colors.labelLight,
            letterSpacing: 1,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}>
            {pool.apyPct30D != null && pool.apyPct30D >= 0
              ? <HiOutlineArrowSmUp size={12} color={colors.green} />
              : <HiOutlineArrowSmDown size={12} color={colors.red} />
            }
            APY 30D
          </span>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11,
            color: pool.apyPct30D != null && pool.apyPct30D >= 0 ? colors.green : colors.red,
            fontWeight: 500,
          }}>
            {pool.apyPct30D != null ? `${pool.apyPct30D > 0 ? "+" : ""}${pool.apyPct30D.toFixed(1)}%` : "\u2014"}
          </span>
        </div>
      </div>

      {/* Risk badge */}
      <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 9,
          fontWeight: 600,
          color: riskStyle.color,
          background: riskStyle.bg,
          border: `1px solid ${riskStyle.border}`,
          padding: "2px 8px",
          borderRadius: 3,
        }}>
          {pool.risk}
        </span>
      </div>
    </motion.div>
  );
}

/* -- Category Section ------------------------------------------------------- */

const sectionVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const cardStaggerVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.97 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: i * 0.08,
      duration: 0.4,
      ease: "easeOut",
    },
  }),
};

function CategorySection({ title, pools, onSelectPool }) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const style = CATEGORY_STYLES[title] || CATEGORY_STYLES["Steady Earners"];
  const scrollRef = useRef(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const checkFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el || isMobile) return;
    setShowLeftFade(el.scrollLeft > 10);
    setShowRightFade(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, [isMobile]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || isMobile) return;
    checkFades();
    el.addEventListener('scroll', checkFades, { passive: true });
    const resizeObs = new ResizeObserver(checkFades);
    resizeObs.observe(el);
    return () => {
      el.removeEventListener('scroll', checkFades);
      resizeObs.disconnect();
    };
  }, [isMobile, checkFades, pools]);

  if (!pools || pools.length === 0) return null;

  return (
    <motion.div
      style={{ marginBottom: isMobile ? 24 : 36 }}
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-40px" }}
    >
      {/* Category header */}
      <div style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        marginBottom: 16,
        flexDirection: "column",
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}>
          <span style={{ fontSize: 24, lineHeight: 1 }}>{style.icon}</span>
          <span style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: isMobile ? 20 : 22,
            color: colors.textPrimary,
          }}>
            {title}
          </span>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            color: style.color,
            background: style.bg,
            border: `1px solid ${style.border}`,
            padding: "3px 10px",
            borderRadius: 10,
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}>
            <CountUp end={pools.length} duration={1.2} preserveValue /> POOLS
          </span>
        </div>
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11,
          color: colors.labelMuted,
          lineHeight: 1.5,
          paddingLeft: 34,
        }}>
          {style.description}
        </span>
      </div>

      {/* Horizontal scroll area with gradient fades */}
      <div style={{ position: "relative" }}>
        {/* Left fade */}
        {!isMobile && showLeftFade && (
          <div style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 48,
            background: `linear-gradient(to right, ${colors.pageBg || '#F8F9FA'}, transparent)`,
            zIndex: 2,
            pointerEvents: "none",
            borderRadius: "12px 0 0 12px",
          }} />
        )}
        {/* Right fade */}
        {!isMobile && showRightFade && (
          <div style={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: 48,
            background: `linear-gradient(to left, ${colors.pageBg || '#F8F9FA'}, transparent)`,
            zIndex: 2,
            pointerEvents: "none",
            borderRadius: "0 12px 12px 0",
          }} />
        )}

        <div
          ref={scrollRef}
          style={{
            display: "flex",
            gap: isMobile ? 12 : 16,
            overflowX: isMobile ? "visible" : "auto",
            paddingBottom: isMobile ? 0 : 8,
            scrollSnapType: isMobile ? "none" : "x mandatory",
            WebkitOverflowScrolling: "touch",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          {pools.map((pool, idx) => (
            <motion.div
              key={pool.id}
              custom={idx}
              variants={cardStaggerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              style={{ scrollSnapAlign: isMobile ? "none" : "start" }}
            >
              <PickCard pool={pool} onSelect={onSelectPool} isMobile={isMobile} index={idx} />
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

/* -- Main Component --------------------------------------------------------- */

export default function BestPicksView({ pools, onSelectPool }) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();

  const categories = useMemo(() => {
    const all = scoredPools(pools);

    const highYieldStable = all
      .filter(p => p.stablecoin)
      .slice(0, 10);

    const volumeKings = [...all]
      .sort((a, b) => b.score.volTvlRatio - a.score.volTvlRatio)
      .filter(p => (p.volume24h || 0) > 10000)
      .slice(0, 10);

    const steadyEarners = [...all]
      .filter(p => p.apyPct7D != null && p.apyPct30D != null)
      .sort((a, b) => {
        const aStability = Math.abs(Math.min(0, a.apyPct7D)) + Math.abs(Math.min(0, a.apyPct30D));
        const bStability = Math.abs(Math.min(0, b.apyPct7D)) + Math.abs(Math.min(0, b.apyPct30D));
        return aStability - bStability;
      })
      .slice(0, 10);

    /* Hidden Gems: high score (>=60) but TVL $100K-$2M */
    const allWithLowTvl = scoredPoolsAllTvl(pools);
    const hiddenGems = allWithLowTvl
      .filter(p => p.score.total >= 60 && p.tvl >= 100000 && p.tvl <= 2000000)
      .slice(0, 10);

    /* Blue Chips: TVL > $10M, stablecoin, consistent APY (30D between -5 and 10) */
    const blueChips = [...all]
      .filter(p =>
        p.tvl > 10000000 &&
        p.stablecoin === true &&
        p.apyPct30D != null &&
        p.apyPct30D >= -5 &&
        p.apyPct30D <= 10
      )
      .sort((a, b) => b.tvl - a.tvl)
      .slice(0, 10);

    /* Rising Stars: apyPct7D > 5 AND apyPct30D > 0 */
    const risingStars = [...all]
      .filter(p =>
        p.apyPct7D != null &&
        p.apyPct30D != null &&
        p.apyPct7D > 5 &&
        p.apyPct30D > 0
      )
      .sort((a, b) => b.apyPct7D - a.apyPct7D)
      .slice(0, 10);

    return { highYieldStable, volumeKings, steadyEarners, hiddenGems, blueChips, risingStars };
  }, [pools]);

  const topScore = useMemo(() => {
    const all = [
      ...categories.highYieldStable,
      ...categories.volumeKings,
      ...categories.steadyEarners,
      ...categories.hiddenGems,
      ...categories.blueChips,
      ...categories.risingStars,
    ];
    if (all.length === 0) return null;
    return all.reduce((best, p) => p.score.total > best.score.total ? p : best, all[0]);
  }, [categories]);

  return (
    <div style={{ padding: isMobile ? "12px 12px" : "20px 24px" }}>
      {/* Hero section */}
      <div style={{
        background: colors.bestPicksBanner,
        borderRadius: 12,
        padding: isMobile ? "20px 16px" : "28px 32px",
        marginBottom: isMobile ? 20 : 28,
        border: `1px solid ${colors.greenBorder}`,
      }}>
        <div style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: isMobile ? 24 : 32,
          color: colors.textPrimary,
          marginBottom: 8,
        }}>
          Best Picks
        </div>
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 12,
          color: colors.textSecondary,
          lineHeight: 1.6,
          maxWidth: 600,
        }}>
          Curated pool recommendations scored by yield sustainability, volume activity,
          TVL depth, and risk profile. Higher scores indicate pools with consistent
          returns and organic trading volume.
        </div>

        {/* Top pick highlight */}
        {topScore && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 10 : 16,
            marginTop: 20,
            paddingTop: 16,
            borderTop: `1px solid ${colors.greenBorder}`,
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}>
            <div>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9,
                color: colors.green,
                letterSpacing: 1.5,
                fontWeight: 600,
                marginBottom: 4,
              }}>
                TOP PICK
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <TokenStack tokens={topScore.tokens} size={22} />
                <span style={{
                  fontFamily: "'DM Serif Display', serif",
                  fontSize: 18,
                  color: colors.textPrimary,
                }}>
                  {topScore.name}
                </span>
              </div>
            </div>
            <div style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: 28,
              color: topScore.apr > 20 ? colors.gold : colors.green,
            }}>
              {(topScore.apr || 0).toFixed(2)}%
            </div>
            <ScoreBar score={topScore.score.total} />
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              color: colors.labelMuted,
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}>
              <ProtocolIcon protocol={topScore.protocol} size={12} /> {topScore.protocol} · <ChainIcon chain={topScore.chain} size={12} /> {topScore.chain}
            </div>
          </div>
        )}
      </div>

      {/* Scoring legend */}
      <div style={{
        display: "flex",
        gap: 20,
        marginBottom: 24,
        flexWrap: "wrap",
      }}>
        {[
          { label: "YIELD STABILITY", weight: "40%", color: colors.green },
          { label: "VOLUME/TVL", weight: "30%", color: colors.gold },
          { label: "TVL DEPTH", weight: "20%", color: "#5470D4" },
          { label: "RISK BONUS", weight: "10%", color: colors.textSecondary },
        ].map(s => (
          <div key={s.label} style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}>
            <div style={{
              width: 8,
              height: 8,
              borderRadius: 2,
              background: s.color,
            }} />
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              color: colors.labelMuted,
              letterSpacing: 0.5,
            }}>
              {s.label}
            </span>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              color: colors.labelLight,
            }}>
              {s.weight}
            </span>
          </div>
        ))}
      </div>

      {/* Category sections */}
      <CategorySection
        title="High Yield Stable"
        pools={categories.highYieldStable}
        onSelectPool={onSelectPool}
      />
      <CategorySection
        title="Volume Kings"
        pools={categories.volumeKings}
        onSelectPool={onSelectPool}
      />
      <CategorySection
        title="Steady Earners"
        pools={categories.steadyEarners}
        onSelectPool={onSelectPool}
      />
      <CategorySection
        title="Hidden Gems"
        pools={categories.hiddenGems}
        onSelectPool={onSelectPool}
      />
      <CategorySection
        title="Blue Chips"
        pools={categories.blueChips}
        onSelectPool={onSelectPool}
      />
      <CategorySection
        title="Rising Stars"
        pools={categories.risingStars}
        onSelectPool={onSelectPool}
      />

      {/* Empty state */}
      {categories.highYieldStable.length === 0 &&
       categories.volumeKings.length === 0 &&
       categories.steadyEarners.length === 0 &&
       categories.hiddenGems.length === 0 &&
       categories.blueChips.length === 0 &&
       categories.risingStars.length === 0 && (
        <div style={{
          textAlign: "center",
          padding: "60px 0",
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 13,
          color: colors.labelLight,
        }}>
          No pools available to score. Waiting for data...
        </div>
      )}
    </div>
  );
}
